import { Link, useLoaderData, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { redirectErrorPage } from '../../utils/errors';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import PersonMatrix from '../../components/common/PersonMatrix/PersonMatrix';
import type {
    PersonMatrixItem,
    PersonMatrixGroup,
    PersonMatrixLegendGroup,
    PersonMatrixLegendItem
} from '../../components/common/PersonMatrix/PersonMatrix';
import { useAttendanceColors } from '../../utils/hooks';
import { getColors, URLKeys } from '../../Constants';
import type {
    AttendanceDetailItem,
    AttendanceSummaryItem,
    ResponsesSession,
    SessionAttendance,
    PartyDetail
} from '../../api';
import { useAppContext } from '../../context/AppContext';
import { DateTime } from 'luxon';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { buildCouncilPath } from '../../utils/councilParams';

function MeetingDetail() {
    const { t, i18n } = useTranslation(['meetings', 'translation']);
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const { bodyType, zastupitelstvoId, zasedaniId } = useParams();
    const municipalitaId = useMunicipalityId();
    const { municipalityData } = useAppContext();
    const timeZone = municipalityData.casova_zona;

    const response = useLoaderData() as [ResponsesSession, SessionAttendance];
    const sessionData = response[0];
    const attendanceData = response[1];

    const attendanceOptions = attendanceData?.sumarizace ?? [];

    const {attendanceOptionLabels} = useLocalizationLabels();
    const attendanceColors = useAttendanceColors();

    const detailByCode = useMemo<Record<string, AttendanceDetailItem | undefined>>(() => {
        return Object.fromEntries((attendanceData?.detail ?? []).map((item) => [item.code, item]));
    }, [attendanceData?.detail]);

    const { attendanceMembers, partyGroups, attendanceLegend, attendanceOverview } = useMemo(() => {
        const ranked: { item: PersonMatrixItem; partyKey: string; statusIndex: number }[] = [];
        const partyGroupMap = new Map<string, PersonMatrixGroup>();
        const partyTally = new Map<string, { present: number; total: number }>();
        const statusLegendItems: PersonMatrixLegendItem[] = [];
        let overallPresent = 0;
        let overallPartial = 0;
        let overallTotal = 0;

        attendanceOptions.forEach((optionItem: AttendanceSummaryItem, statusIndex: number) => {
            const detailGroup = detailByCode[optionItem.code];
            const statusLabel = capitalizeFirstLetter(attendanceOptionLabels[optionItem.id] ?? optionItem.code);
            const ringColor = attendanceColors[optionItem.code as keyof typeof attendanceColors] ?? getColors().darkGray;
            const isPresent = optionItem.code === 'PRESENT';
            const isPartial = optionItem.code === 'PART_PRESENT';
            statusLegendItems.push({ color: ringColor, label: statusLabel, ring: true });

            detailGroup?.strany?.forEach((party: PartyDetail) => {
                if (!partyGroupMap.has(party.strana_zkratka)) {
                    partyGroupMap.set(party.strana_zkratka, {
                        key: party.strana_zkratka,
                        label: party.strana_zkratka,
                        color: party.strana_barva
                    });
                }
                const tally = partyTally.get(party.strana_zkratka) ?? { present: 0, total: 0 };

                party.clenove?.forEach((member: any) => {
                    tally.total += 1;
                    overallTotal += 1;
                    if (isPresent) {
                        tally.present += 1;
                        overallPresent += 1;
                    } else if (isPartial) {
                        overallPartial += 1;
                    }

                    ranked.push({
                        partyKey: party.strana_zkratka,
                        statusIndex,
                        item: {
                            id: member.clen_id,
                            fullName: `${member.clen_jmeno} ${member.clen_prijmeni}`,
                            caption: member.clen_prijmeni,
                            ringColor,
                            dotColor: party.strana_barva,
                            photoUrl: member.clen_photo,
                            linkTo: buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'zastupitele', member.clen_id),
                            groupKey: party.strana_zkratka,
                            meta: [
                                { color: party.strana_barva, label: party.strana_zkratka },
                                { color: ringColor, label: statusLabel }
                            ]
                        }
                    });
                });

                partyTally.set(party.strana_zkratka, tally);
            });
        });

        const attendanceMembers = ranked
            .sort((a, b) => a.partyKey.localeCompare(b.partyKey) || a.statusIndex - b.statusIndex)
            .map((r) => r.item);

        const partyGroups = Array.from(partyGroupMap.values())
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((group) => {
                const tally = partyTally.get(group.key);
                const stat =
                    tally && tally.total > 0 ? formatter.percentFrom100((tally.present / tally.total) * 100) : undefined;
                return { ...group, stat };
            });

        const attendanceLegend: PersonMatrixLegendGroup[] = [
            { label: t('attendance'), items: statusLegendItems },
            {
                label: t('parties', { ns: 'parties' }),
                items: partyGroups.map((g) => ({ color: g.color ?? '', label: g.label }))
            }
        ];

        const attendanceOverview =
            overallTotal > 0
                ? overallPartial > 0
                    ? t('attendance.overviewWithPartial', {
                          ns: 'analyses',
                          count: overallTotal,
                          percent: formatter.percentFrom100((overallPresent / overallTotal) * 100),
                          partialPercent: formatter.percentFrom100((overallPartial / overallTotal) * 100)
                      })
                    : t('attendance.overview', {
                          ns: 'analyses',
                          count: overallTotal,
                          percent: formatter.percentFrom100((overallPresent / overallTotal) * 100)
                      })
                : null;

        return { attendanceMembers, partyGroups, attendanceLegend, attendanceOverview };
    }, [attendanceOptions, detailByCode, attendanceOptionLabels, attendanceColors, municipalitaId, bodyType, zastupitelstvoId, t, formatter]);

    if (sessionData !== undefined && attendanceData !== undefined) {
        if (!sessionData?.cislo_zasedani) {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
            return null;
        }
    }

    const percentageAttendance = formatter.percent2From100(sessionData.attendance);
    const mutedAttendenceString = `(${t('MeetingDetail.x-outOf-y-member', {
        x: sessionData.ucast,
        y: sessionData.celkem,
        count: sessionData.celkem
    })})`;
    const dtMunicipalityFrom = DateTime.fromISO(sessionData?.datum_od, { zone: timeZone });
    const dtMunicipalityTo = DateTime.fromISO(sessionData?.datum_do ?? '', { zone: timeZone });
    
    const formattedDateFrom = dtMunicipalityFrom.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL);
    const formattedDateTo = dtMunicipalityTo.isValid
        ? dtMunicipalityTo.setLocale(currentLocale).toLocaleString(DateTime.DATE_FULL)
        : null;

    let sessionDate = formattedDateFrom;
    if (formattedDateTo) {
        sessionDate = `${formattedDateFrom} – ${formattedDateTo}`;
    }

    const votingsPath = buildCouncilPath(municipalitaId, bodyType, zastupitelstvoId, 'hlasovani');
    const votingsLinkFor = (voteResult?: '1' | '2') => {
        // The "cislo_zasedani" votes-list filter actually matches Zasedani.id (the route's :zasedaniId),
        // not sessionData.cislo_zasedani (the human-facing session number shown in the page title).
        const params = new URLSearchParams({ [URLKeys.meetingNumber]: String(zasedaniId) });
        if (voteResult) {
            params.set(URLKeys.voteResult, voteResult);
        }
        return `${votingsPath}?${params.toString()}`;
    };

    return (
        <>
            <div className='page-header-row'>
                <ContentSection
                    title={`${t('meetingNo', {num: sessionData.cislo_zasedani})}`}
                    level={1}
                    subtitle={`${sessionDate}`}
                    className='page-title-section'
                    showMoreChevronLinkProps={
                        sessionData?.video_zaznam[0]?.url
                            ? {
                                    text: `${capitalizeFirstLetter(t('MeetingDetail.recordAndMaterials'))}`,
                                    link: `${sessionData.video_zaznam[0].url}`
                                }
                            : undefined
                    }
                />
                <div className='stat-chip-row'>
                    <StatChip
                        icon='group'
                        value={percentageAttendance}
                        label={t('attendance')}
                        mutedText={mutedAttendenceString}
                    />
                    <Link to={votingsLinkFor()} className='stat-chip-link'>
                        <StatChip
                            icon='stacks'
                            value={sessionData.pocet_hlasovani.platna}
                            label={t('validVotings', { ns: 'votings' })}
                        />
                    </Link>
                    <Link to={votingsLinkFor('1')} className='stat-chip-link'>
                        <StatChip
                            icon='check_box'
                            value={sessionData.pocet_hlasovani.prijata}
                            label={t('approvedProposals', { ns: 'votings' })}
                        />
                    </Link>
                    <Link to={votingsLinkFor('2')} className='stat-chip-link'>
                        <StatChip
                            icon='disabled_by_default'
                            value={sessionData.pocet_hlasovani.neprijata}
                            label={t('rejectedProposals', { ns: 'votings' })}
                        />
                    </Link>
                </div>
            </div>

            <ContentSection level={3} title={t('attendance.councilMembers', {ns: 'analyses'})} subtitle={t('attendance.tooltip',{ns: 'analyses'})} className='dashboard-card'>
                {attendanceOverview && <div className='page-description'>{attendanceOverview}</div>}
                <PersonMatrix items={attendanceMembers} groups={partyGroups} legend={attendanceLegend} />
            </ContentSection>
        </>
    );
}

export default MeetingDetail;
